import { useEffect } from 'react';
import styles from '../../../common/Drawer/Drawer.module.scss';

const shouldNotNavigate = (e) => {
  const { activeElement } = document;

  const expandableElements = Array.from(document.querySelectorAll('[aria-expanded=true]'));
  // Get elements with aria-hidden=false, but exclude those within collapsed or hidden containers
  const hidableElements = Array.from(document.querySelectorAll('[aria-hidden=false]'))
    .filter(element => !element.closest('[aria-hidden="true"]'));

  // @ts-ignore
  if (['INPUT', 'TEXTAREA'].includes(activeElement.tagName) || activeElement.contentEditable === 'true') return true;
  // AIDEV-NOTE: must stay above the button check below, which returns early and lets arrows through.
  // A keyboard drag in a popup table focuses a handle react-beautiful-dnd gives role="button", so the
  // arrows that move the row also walked the builder to the next slide — unmounting the popup when
  // that slide had no template (PLAT-18106 follow-up). No dialog should drive slide navigation.
  if (activeElement.closest('[role="dialog"]')) return true;
  if (activeElement.getAttribute('role') === 'button' || ['A', 'BUTTON'].includes(activeElement.tagName)) {
    return !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key);
  }
  if (activeElement.classList.contains('fr-element')) return true;

  if (e?.target?.getAttribute?.('data-draggable-location') === 'dropzone') return true;
  // A keyboard drag in the slide panel uses the same arrow keys.
  if (document.querySelector('#slide-panel[data-drag-active]')) return true;
  if (activeElement.id.includes('pano')) return true;
  if (document.fullscreenElement && document.fullscreenElement.id.includes('pano')) return true;

  if (expandableElements.length > 0) return true;
  if (hidableElements.length > 0) {
    if (hidableElements.every(el => el.classList.contains(styles.drawer) || el.id.includes('wm'))) return false;

    return true;
  }

  return false;
};

export default function useKeyboardNavigation({
  disabled = false,
  numElements,
  onBack,
  onForward,
  position,
  forwardKeys = ['ArrowDown', 'ArrowRight'],
  backKeys = ['ArrowUp', 'ArrowLeft'],
}) {
  const onKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (shouldNotNavigate(e)) return;

    const canGoForward = position !== numElements;
    const canGoBack = position !== 1;

    if (forwardKeys.includes(e.key) && canGoForward) onForward();
    else if (backKeys.includes(e.key) && canGoBack) onBack();
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown, false);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [disabled, position, numElements, onBack, onForward]);
}
